import { useState } from "react";
import { Globe, ExternalLink, CheckCircle2, Search, AlertCircle, Star, Building2, ShieldCheck, ChevronDown } from "lucide-react";

const STATUS_STYLE = {
  VERIFIED_LISTING: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  LISTING_FOUND: { cls: "bg-blue-50 text-blue-700 border-blue-200", icon: CheckCircle2 },
  LIKELY_MATCH: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Search },
  LISTING_NOT_FOUND: { cls: "bg-slate-100 text-slate-500 border-slate-200", icon: Search },
  ACCESS_NOT_CONFIGURED: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertCircle },
  NO_DATA: { cls: "bg-slate-100 text-slate-500 border-slate-200", icon: AlertCircle },
};

const TYPE_LABEL = {
  maps_directory: "Maps / Directory",
  directory: "Directory",
  review_platform: "Review platform",
  social_review: "Social",
  general_reputation: "Reputation",
  government_registry: "Official registry",
  publication: "Publication",
  product_review: "Product reviews",
};

const INTEG = {
  LIVE: { label: "Live API", cls: "text-emerald-600" },
  NOT_CONFIGURED: { label: "API not connected", cls: "text-slate-400" },
  MANUAL: { label: "Manual check", cls: "text-slate-400" },
};

function SourceRow({ s, idx }) {
  const st = STATUS_STYLE[s.listing_status] || STATUS_STYLE.LISTING_NOT_FOUND;
  const Icon = st.icon;
  return (
    <div className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 p-3.5" data-testid={`industry-source-${idx}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-900">{s.platform}</span>
          {s.primary && <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-300" title="Primary source for this industry" />}
          <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">{TYPE_LABEL[s.source_type] || s.source_type}</span>
        </div>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${st.cls}`} data-testid={`industry-source-status-${idx}`}>
            <Icon className="w-3 h-3" /> {s.listing_status_label}
          </span>
          {s.metrics?.rating != null && (
            <span className="text-[12px] text-slate-600 font-medium">★ {s.metrics.rating}{s.metrics.review_count != null ? ` · ${s.metrics.review_count} reviews` : ""}</span>
          )}
          <span className={`text-[11px] ${(INTEG[s.integration_status] || INTEG.MANUAL).cls}`}>{(INTEG[s.integration_status] || INTEG.MANUAL).label}</span>
        </div>
        {s.known_limitations && s.listing_status === "ACCESS_NOT_CONFIGURED" && (
          <p className="mt-1 text-[11px] text-slate-400">{s.known_limitations}</p>
        )}
      </div>
      {(s.verify_url || s.setup_url) && (
        <a href={s.verify_url || s.setup_url} target="_blank" rel="noopener noreferrer"
          className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12px] font-medium text-slate-600 hover:border-slate-300 hover:text-slate-900 transition-colors"
          data-testid={`industry-source-link-${idx}`}>
          {s.matched_url ? "View" : "Check"} <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
    </div>
  );
}

export function IndustrySources({ data }) {
  const [showAll, setShowAll] = useState(false);
  if (!data || !data.enabled) return null;
  const sources = data.sources || [];
  const shown = showAll ? sources : sources.slice(0, 6);
  const s = data.summary || {};
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm" data-testid="industry-sources">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center w-10 h-10 rounded-xl bg-blue-600 text-white"><Globe className="w-5 h-5" /></span>
        <div>
          <h3 className="font-heading text-xl font-bold text-slate-900">Industry Listings & Review Sources</h3>
          <p className="text-[12px] text-slate-500">{data.industry_label} · v{data.model_version}</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3" data-testid="industry-sources-coverage">
          <p className="text-2xl font-bold text-slate-900">{s.primary_listings_found ?? 0}/{s.primary_sources ?? 0}</p>
          <p className="text-[11px] text-slate-500">Primary sources with a listing</p>
        </div>
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
          <p className="text-2xl font-bold text-slate-900">{s.relevant_sources ?? sources.length}</p>
          <p className="text-[11px] text-slate-500">Relevant sources</p>
        </div>
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
          <p className="text-2xl font-bold text-emerald-600">{s.live_integrations ?? 0}</p>
          <p className="text-[11px] text-slate-500">Live API sources</p>
        </div>
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 flex items-center">
          <p className="text-[12px] text-slate-600 flex items-center gap-1.5"><Building2 className="w-4 h-4 text-slate-400" /> {s.coverage_label}</p>
        </div>
      </div>

      <div className="mt-4 space-y-2.5">
        {shown.map((src, i) => <SourceRow key={src.id} s={src} idx={i} />)}
      </div>
      {sources.length > 6 && (
        <button onClick={() => setShowAll((v) => !v)} data-testid="industry-sources-toggle"
          className="mt-3 inline-flex items-center gap-1 text-[13px] font-medium text-blue-600 hover:text-blue-700">
          {showAll ? "Show fewer" : `Show all ${sources.length} sources`} <ChevronDown className={`w-4 h-4 transition-transform ${showAll ? "rotate-180" : ""}`} />
        </button>
      )}

      <div className="mt-5 flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 p-3">
        <ShieldCheck className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
        <p className="text-[11px] text-slate-500">{data.disclaimer}</p>
      </div>
    </div>
  );
}

export default IndustrySources;
